import React from "react";
import { ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";
import { C, RARITY } from "../../constants/design";
import { SLOT, STATS, STAT_DISPLAY_ORDER, SELL_PRICE, FAMILIER_IMAGES, ITEM_IMAGES } from "../../constants/items";
import { StatIcon } from "./StatIcon";
import { getStats, iName, iSkin } from "../../engine/itemEngine";
import type { Item } from "../../types/game";

const COIN_ICON = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/CoinIcon.png";

// Fonction pour récupérer les couleurs spécifiques de rareté
const getRarityConfig = (rarity: string, fallbackColor: string) => {
  const rLower = String(rarity).toLowerCase();
  if (rLower.includes("legend") || rLower.includes("légend")) {
    return { radial: "#442800", innerGlow: "#FFBF00" };
  }
  if (rLower.includes("epic") || rLower.includes("épiq")) {
    return { radial: "#3E0060", innerGlow: "#CC23FF" };
  }
  if (rLower.includes("rare")) {
    return { radial: "#001351", innerGlow: "#0084FF" };
  }
  return { radial: `${fallbackColor}40`, innerGlow: fallbackColor };
};

export function ItemModal({
  it,
  isEquipped,
  equippedItem,
  onEquip,
  onUnequip,
  onSell,
  onStore,
  onClose,
  readOnly,
}: {
  it: Item | null;
  isEquipped?: boolean;
  equippedItem?: Item | null;
  onEquip: (it: Item) => void;
  onUnequip: (it: Item) => void;
  onSell: (it: Item) => void;
  onStore?: (it: Item) => void; // ranger dans le coffre (items non équipés)
  onClose: () => void;
  readOnly?: boolean;
}) {
  if (!it) return null;
  const { t } = useI18n();
  const [confirmSell, setConfirmSell] = React.useState(false);
  const r = RARITY[it.rarity];
  const slotDef = SLOT[it.slot] || { emoji: "?" };
  const ownStats = getStats(it) as Record<string, number>;
  const equippedStats = (equippedItem ? getStats(equippedItem) : {}) as Record<string, number>;
  const ownKeys = new Set(Object.keys(ownStats));
  // En mode comparaison, on affiche aussi les stats que l'item équipé possède
  // mais que le nouvel item n'a pas : elles apparaissent à 0 avec un delta
  // négatif (et une opacité réduite) pour montrer ce qu'on perdrait.
  const compareKeys =
    !isEquipped && equippedItem
      ? Array.from(new Set([...Object.keys(ownStats), ...Object.keys(equippedStats)]))
      : Object.keys(ownStats);
  const statEntries = compareKeys
    .map((key) => [key, ownStats[key] ?? 0] as [string, number])
    .sort(([a], [b]) => STAT_DISPLAY_ORDER.indexOf(a) - STAT_DISPLAY_ORDER.indexOf(b));
  // L'énergie (affixe ballon) ne compte pas dans le total de power de l'item.
  const totalStats = statEntries.reduce((acc, [k, val]) => acc + (k === "energy" ? 0 : val), 0);
  const equippedTotal = Object.values(equippedStats).reduce((a, b) => a + b, 0);
  const totalDelta = totalStats - equippedTotal;

  const { radial, innerGlow } = getRarityConfig(it.rarity, r.color);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: `radial-gradient(circle at 50% 0%, ${radial} 0%, #000000 80%)`,
          backgroundColor: "#000000",
          boxShadow: `inset 0px 1px 0px 0px ${innerGlow}`,
          border: "none",
          borderRadius: "24px 24px 0 0",
          padding: "12px 20px 36px",
          width: "100%",
          maxWidth: 430,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Ligne horizontale supérieure */}
        <div
          style={{
            width: 40,
            height: 4,
            background: "rgba(255,255,255,0.2)",
            borderRadius: 999,
            margin: "0 auto 16px",
          }}
        />

        {/* Bouton Fermer */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.1)",
            background: "transparent",
            color: "rgba(255,255,255,0.5)",
            display: "grid",
            placeItems: "center",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {/* 1. L'IMAGE */}
        <div
          style={{
            position: "relative",
            display: "grid",
            placeItems: "center",
            width: 120,
            height: 120,
            marginTop: 10,
            marginBottom: 20,
          }}
        >
          {it.slot === "familier" && FAMILIER_IMAGES[iSkin(it)] ? (
            <img
              src={FAMILIER_IMAGES[iSkin(it)]}
              alt=""
              style={{
                width: 120,
                height: 120,
                objectFit: "contain",
                position: "relative",
                zIndex: 2,
                filter: `drop-shadow(0 8px 20px rgba(0,0,0,0.5))`,
              }}
            />
          ) : ITEM_IMAGES[iSkin(it)] ? (
            <img
              src={ITEM_IMAGES[iSkin(it)]}
              alt=""
              style={{
                width: 120,
                height: 120,
                objectFit: "contain",
                position: "relative",
                zIndex: 2,
                filter: `drop-shadow(0 8px 20px rgba(0,0,0,0.5))`,
              }}
            />
          ) : (
            <span
              style={{
                fontSize: 64,
                filter: `drop-shadow(0 8px 20px rgba(0,0,0,0.5))`,
                position: "relative",
                zIndex: 2,
                lineHeight: 1,
              }}
            >
              {slotDef.emoji}
            </span>
          )}
        </div>

        {/* 2. LE BADGE DE RARETÉ */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 900,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#000",
            background: r.color || innerGlow,
            borderRadius: 999,
            padding: "4px 14px",
            marginBottom: 12,
          }}
        >
          {r.label}
        </span>

        {/* 3. LE TITRE */}
        <span
          style={{
            fontSize: 22,
            fontWeight: 900,
            color: "#fff",
            textAlign: "center",
            marginBottom: it.cosmetic ? 8 : 20,
          }}
        >
          {iSkin(it)}
        </span>

        {it.cosmetic && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(255,255,255,0.45)",
              background: "rgba(255,255,255,0.08)",
              borderRadius: 999,
              padding: "3px 10px",
              marginBottom: 16,
            }}
          >
            ✨ Appearance Changed · Original: {iName(it)}
          </div>
        )}

        {/* 4. LE BLOC DES STATISTIQUES */}
        <div
          style={{
            width: "100%",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: 10,
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {statEntries.map(([key, val]) => {
            const sd = STATS.find((s) => s.key === key);
            const isBlessure = key === "blessure";
            const isEnergy = key === "energy";
            const iconColor = isBlessure ? "#e06a8c" : isEnergy ? "#F5B301" : sd?.color || "#3df87a";
            const fullName = isBlessure
              ? t("itemModal.defense")
              : t(`items.stats.${key}`);
            const eqVal = equippedStats[key] ?? 0;
            const delta = isEquipped ? 0 : val - eqVal;
            const deltaColor = delta > 0 ? "#3df87a" : delta < 0 ? "#f87171" : "rgba(255,255,255,0.3)";
            const deltaLabel = delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : "0";
            // Stat que le nouvel item ne possède pas mais que l'équipé a : ligne atténuée.
            const isMissing = !ownKeys.has(key);

            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  opacity: isMissing ? 0.5 : 1,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {isBlessure
                    ? <ShieldCheck size={16} color={iconColor} />
                    : isEnergy
                    ? <span style={{ fontSize: 20, lineHeight: 1 }}>💧</span>
                    : sd && <StatIcon st={sd} size={22} />
                  }
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{val}</span>
                  <span style={{ fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,0.6)" }}>{fullName}</span>
                </div>
                {!isEquipped && (
                  <span style={{ fontSize: 14, fontWeight: 700, color: deltaColor, minWidth: 32, textAlign: "right" }}>
                    {deltaLabel}
                  </span>
                )}
              </div>
            );
          })}

          {!isEquipped && (
            <>
              <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "-4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>{t("itemModal.total")}</span>
                <span style={{
                  fontSize: 14, fontWeight: 800,
                  color: totalDelta > 0 ? "#3df87a" : totalDelta < 0 ? "#f87171" : "rgba(255,255,255,0.3)",
                }}>
                  {totalDelta > 0 ? `+${totalDelta}` : totalDelta < 0 ? `${totalDelta}` : t("itemModal.same")}
                </span>
              </div>
            </>
          )}
        </div>

        {/* 5. LES BOUTONS */}
        {!readOnly && (
          confirmSell ? (
            <div style={{ width: "100%", marginTop: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", textAlign: "center" }}>{t("itemModal.sellConfirmTitle")}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", textAlign: "center", marginBottom: 4 }}>{t("itemModal.sellConfirmSub")}</div>
              <div style={{ display: "flex", gap: 12, width: "100%" }}>
                <button
                  onClick={() => setConfirmSell(false)}
                  style={{
                    flex: 1, padding: "16px", borderRadius: 14, border: "none",
                    background: "rgba(255,255,255,0.12)", color: "#fff",
                    fontWeight: 600, fontSize: 16, cursor: "pointer",
                  }}
                >
                  {t("itemModal.cancel")}
                </button>
                <button
                  onClick={() => { onSell(it); onClose(); }}
                  style={{
                    flex: 1, padding: "16px", borderRadius: 14, border: "none",
                    background: "#ef4444", color: "#fff",
                    fontWeight: 700, fontSize: 16, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  {t("itemModal.sell")}
                  <div style={{ display: "flex", alignItems: "center", gap: 3, color: "#FFF5AC", fontSize: 14 }}>
                    {SELL_PRICE[it.rarity]}
                    <img src={COIN_ICON} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div style={{ width: "100%", marginTop: 20, display: "flex", gap: 12 }}>
              <button
                onClick={() => setConfirmSell(true)}
                style={{
                  flex: "0 0 35%",
                  padding: "16px",
                  borderRadius: 14,
                  border: "none",
                  background: "rgba(255, 255, 255, 0.18)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: 16 }}>{t("itemModal.sell")}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 2, color: "#FFF5AC", fontWeight: 600, fontSize: 14 }}>
                  {SELL_PRICE[it.rarity]}
                  <img src={COIN_ICON} alt="Coin" style={{ width: 20, height: 20, objectFit: "contain" }} />
                </div>
              </button>

              {isEquipped ? (
                <button
                  onClick={() => { onUnequip(it); onClose(); }}
                  style={{
                    flex: 1, padding: "16px", borderRadius: 14, border: "none",
                    background: "rgba(255, 255, 255, 0.18)", color: "#fff",
                    fontWeight: 600, fontSize: 16, cursor: "pointer",
                  }}
                >
                  {t("itemModal.unequip")}
                </button>
              ) : (
                <button
                  onClick={() => { onEquip(it); onClose(); }}
                  style={{
                    flex: 1, padding: "16px", borderRadius: 14, border: "none",
                    background: "#3df87a", color: "#0b0710",
                    fontWeight: 600, fontSize: 16, cursor: "pointer",
                  }}
                >
                  {t("itemModal.equip")}
                </button>
              )}

              {!isEquipped && onStore && (
                <button
                  onClick={() => { onStore(it); onClose(); }}
                  title="Store in vault"
                  style={{
                    flex: "0 0 56px", padding: "16px 0", borderRadius: 14, border: "none",
                    background: "rgba(249,226,175,0.18)", color: "#f9e2af",
                    fontWeight: 700, fontSize: 18, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  📦
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}